import { useState, useEffect, useContext } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Toolbar,
  Typography,
  IconButton,
  AppBar,
  Avatar,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useNavigate, useLocation, Outlet } from "react-router-dom";

// Ícones
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import UploadIcon from "@mui/icons-material/Upload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HandymanIcon from "@mui/icons-material/Handyman";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import ConstructionIcon from "@mui/icons-material/Construction";
import BuildIcon from "@mui/icons-material/Build";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import ColorModeContext from "../ColorModeContext";
import { useAuth } from "../auth/AuthContext";
import { ROTULO_NIVEL } from "../auth/permissions";

const drawerWidth = 240;

// Extrai as iniciais do nome para o avatar (ex.: "João Silva" → "JS").
function iniciais(nome = "") {
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 0 || !partes[0]) return "?";
  const primeira = partes[0][0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
}

/**
 * Configuração declarativa do menu.
 *
 * Por que assim? Porque é MUITO mais fácil manter: para adicionar um módulo novo
 * no futuro, basta incluir um objeto aqui. A estrutura JSX abaixo renderiza
 * todos os itens automaticamente. Isso é o padrão "data-driven UI".
 *
 * `enabled: false` => vira "botão fantasma" (clicável mas leva para página
 * ComingSoon e visualmente opaco).
 */
// `modulo` liga o item à matriz de permissões. Itens sem `modulo`
// (ex.: Início) aparecem para todo usuário autenticado.
const menuItems = [
  { label: "In\u00edcio", icon: <HomeIcon />, path: "/", enabled: true },
  { label: "Equipamentos", icon: <ConstructionIcon />, children: [
    { label: "Cadastro de equipamentos", icon: <BuildIcon />, path: "/equipamentos", modulo: "equipamentos" },
    { label: "Manuten\u00e7\u00e3o", icon: <BuildIcon />, path: "/equipamentos/manutencao", modulo: "equipamentos" },
    { label: "Equipamentos Alocados", icon: <LocalShippingIcon />, path: "/administracao/acompanhamento-equipamentos", modulo: "acompanhamento_equipamentos" },
    { label: "Servi\u00e7os", icon: <HandymanIcon />, path: "/servicos", modulo: "servicos" }
  ] },
  { label: "Almoxarifado", icon: <WarehouseIcon />, children: [
    { label: "Produtos", icon: <Inventory2Icon />, path: "/produtos", modulo: "produtos" },
    { label: "Almoxarifados", icon: <WarehouseIcon />, path: "/almoxarifados", modulo: "almoxarifados" },
    { label: "Sa\u00eddas", icon: <UploadIcon />, path: "/saidas", modulo: "saidas" },
    { label: "Compras", icon: <AssignmentIcon />, path: "/compras", modulo: "compras" },
    { label: "Fornecedores", icon: <StorefrontIcon />, path: "/fornecedores", modulo: "fornecedores" }
  ] },
  { label: "Administra\u00e7\u00e3o", icon: <PeopleIcon />, children: [
    { label: "Controle de Alojamentos", icon: <WarehouseIcon />, path: "/alojamentos", modulo: "alojamentos" },
    { label: "Funcion\u00e1rios", icon: <PeopleIcon />, path: "/funcionarios", modulo: "funcionarios" },
    { label: "Equipes", icon: <GroupsIcon />, path: "/equipes", modulo: "equipes" },
    { label: "Gastos por Aplica\u00e7\u00e3o", icon: <AssessmentIcon />, path: "/gastos-aplicacoes", modulo: "gastos_aplicacoes" },
    { label: "Solicita\u00e7\u00f5es", icon: <HowToRegIcon />, path: "/solicitacoes", centralOnly: true }
  ] }
];

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggle } = useContext(ColorModeContext);
  const { user, logout, podeVer } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [now, setNow] = useState(new Date());
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Mostra apenas os itens que o nível do usuário pode ver.
  const permitido = item => item.centralOnly ? user?.access_level === "CENTRAL" : !item.modulo || podeVer(item.modulo);
  const itensVisiveis = menuItems.flatMap(item => {
    if (!item.children) return permitido(item) ? [item] : [];
    const children = item.children.filter(permitido);
    return children.length ? [{ ...item, children }] : [];
  });

  function handleLogout() {
    setUserMenuAnchor(null);
    logout();
    navigate("/login", { replace: true });
  }

  /**
   * Relógio que atualiza a cada segundo.
   * `useEffect` com setInterval. Muito importante: o return do useEffect
   * precisa limpar o interval, senão ele continua rodando mesmo quando o
   * componente é desmontado (leak de memória).
   */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const activeGroup = menuItems.find(item => item.children?.some(child => location.pathname === child.path || location.pathname.startsWith(`${child.path}/`)));
    if (activeGroup) setExpandedGroups(current => ({ ...current, [activeGroup.label]: true }));
  }, [location.pathname]);

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  /**
   * Função que decide se um item do menu está "ativo" (destacado em vermelho).
   * Para "/" precisa ser comparação exata, senão TODA rota começa com "/" e
   * tudo ficaria ativo. Para as outras, basta começar com o path.
   */
  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";

    // Manutenção é uma área própria dentro de Equipamentos. Sem essa
    // separação, o prefixo "/equipamentos" também deixava os dois itens
    // do menu destacados ao abrir a manutenção.
    if (path === "/equipamentos") {
      return location.pathname.startsWith(path) && !location.pathname.includes("/manutencao");
    }
    if (path === "/equipamentos/manutencao") {
      return location.pathname.includes("/manutencao");
    }

    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Espaçamento sob a AppBar no modo desktop */}
      <Toolbar sx={{ display: { xs: "none", sm: "block" } }} />

      {/* Lista principal de navegação */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {itensVisiveis.map((item) => {
          if (item.children) {
            const groupActive = item.children.some(child => isActive(child.path));
            const expanded = expandedGroups[item.label] ?? groupActive;
            return <Box key={item.label}>
              <ListItemButton onClick={() => setExpandedGroups(current => ({ ...current, [item.label]: !(current[item.label] ?? groupActive) }))} aria-expanded={expanded}>
                <ListItemIcon sx={{ color: groupActive ? "primary.main" : "text.secondary", minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: groupActive ? 600 : 500 }} />
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </ListItemButton>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {item.children.map(child => {
                    const active = isActive(child.path);
                    return <ListItemButton key={child.path} onClick={() => { navigate(child.path); setDrawerOpen(false); }} aria-current={active ? "page" : undefined} sx={{ pl: 4, bgcolor: active ? "rgba(239,68,68,0.12)" : "transparent", color: active ? "primary.main" : "text.primary", "&:hover": { bgcolor: active ? "rgba(239,68,68,0.18)" : "rgba(255,255,255,0.05)" } }}>
                      <ListItemIcon sx={{ color: active ? "primary.main" : "text.secondary", minWidth: 36 }}>{child.icon}</ListItemIcon>
                      <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 13, fontWeight: active ? 600 : 500 }} />
                    </ListItemButton>;
                  })}
                </List>
              </Collapse>
            </Box>;
          }
          const active = isActive(item.path);
          return <ListItemButton key={item.path} onClick={() => { navigate(item.path); setDrawerOpen(false); }} sx={{ bgcolor: active ? "rgba(239,68,68,0.12)" : "transparent", color: active ? "primary.main" : "text.primary" }}>
            <ListItemIcon sx={{ color: active ? "primary.main" : "text.secondary", minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }} />
          </ListItemButton>;
        })}
      </List>

      {/* Rodapé da sidebar: data e hora atuais (como na imagem de referência) */}
      <Divider />
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: "rgba(239,68,68,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 18, color: "primary.main" }} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Data e hora
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {now.toLocaleDateString("pt-BR")} {now.toLocaleTimeString("pt-BR")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* TOPBAR */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "background.paper",
          // Sem isso o AppBar usa texto branco (cor "primary"), que some no
          // tema claro. Herdamos a cor de texto do tema atual.
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <Toolbar>
          {/* Botão hamburger — só aparece no mobile */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <Typography
            variant="h6"
            onClick={() => navigate("/")}
            noWrap
            sx={{
              cursor: "pointer",
              flexGrow: 1,
              fontWeight: 700,
              // Menor no celular para não espremer os ícones da direita.
              fontSize: { xs: "1rem", sm: "1.25rem" },
              "&:hover": { opacity: 0.85 }
            }}
          >
            Sistema de Almoxarifado
          </Typography>

          {/* Ações do topo direito */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={mode === "dark" ? "Tema claro" : "Tema escuro"}>
              <IconButton color="inherit" size="small" onClick={toggle}>
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Conta">
              <Box
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1, cursor: "pointer" }}
              >
                <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
                  {iniciais(user?.nome)}
                </Avatar>
                <Box sx={{ display: { xs: "none", md: "block" }, lineHeight: 1.2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Olá, {user?.nome || "Usuário"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ROTULO_NIVEL[user?.access_level] || user?.access_level || "—"}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>

            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" fontWeight={600}>{user?.nome}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{user?.email}</Typography>
                {user?.almoxarifado && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Almoxarifado: {user.almoxarifado}
                  </Typography>
                )}
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <MenuItemIcon>
                  <LogoutIcon fontSize="small" />
                </MenuItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer desktop (fixo à esquerda) */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: "none", sm: "block" },
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "background.paper",
            borderRight: "1px solid rgba(255,255,255,0.06)"
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Drawer mobile (abre e fecha) */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }} // melhor performance no mobile
        sx={{
          display: { xs: "block", sm: "none" },
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: "background.paper"
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Área principal de conteúdo */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: "64px",
          width: { sm: `calc(100% - ${drawerWidth}px)` }
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
